import { Megaphone, Zap } from "lucide-react";
import {
  ChalkActionButton, ChalkCard, ChalkIllustrationLines,
  EmptyState, LineupStatTile, SportBadge,
} from "../primitives";
import EventWeatherBadge from "../EventWeatherBadge";
import { formatEventWhen } from "../../../lib/eventTime";

function humanWhen(event, user) {
  // Wraps formatEventWhen with relative "Today · 6pm" / "Tomorrow · 6pm"
  // prefixes — the same logic as Playbook's humanWhen, kept here as a
  // helper so the NextPlayCard hero stays compact.
  if (!event) return "";
  const base = formatEventWhen(event, user);
  if (!base) return "";
  const d = new Date(event.utc_time || event.start_utc || event.local_time);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) {
    const time = base.split("·").slice(1).join("·").trim();
    return `Today · ${time}`;
  }
  if (sameDay(d, tomorrow)) {
    const time = base.split("·").slice(1).join("·").trim();
    return `Tomorrow · ${time}`;
  }
  return base;
}

/**
 * Hero card on the Chalkboard — surfaces the next upcoming event with roster
 * counts and primary CTAs (Send Chalk Talk, Erase and Redraw). Falls back to
 * an EmptyState when no event exists.
 */
export function NextPlayCard({
  nextPlay, activeTeam, athletes, isChalkboard, cancelling, user,
  onErase, onSendTalk, onAddFirstPlay,
}) {
  if (!nextPlay) {
    return (
      <EmptyState
        icon={<ChalkIllustrationLines />}
        headline="The board is yours, Coach."
        body="Add your first play to build out the season."
        ctaLabel="Add your first play"
        onCta={onAddFirstPlay}
      />
    );
  }
  return (
    <ChalkCard data-testid="next-play-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: "var(--chalk-board)" }}>{nextPlay.title}</div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
            {humanWhen(nextPlay, user)} · {nextPlay.location || "TBD"}
          </div>
          <div style={{ marginTop: 6 }}>
            <EventWeatherBadge eventId={nextPlay.id} />
          </div>
        </div>
        <SportBadge sport={activeTeam?.sport} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 14 }}>
        <LineupStatTile number={athletes} label="Roster" />
        <LineupStatTile number={"—"} label="Going" />
        <LineupStatTile number={"—"} label="Late" />
        <LineupStatTile number={"—"} label="No" />
      </div>
      <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
        <ChalkActionButton testId="next-play-message" variant="ghost" label="Send Chalk Talk" icon={<Megaphone size={16} aria-hidden="true" />} onClick={onSendTalk} />
        {isChalkboard && (
          <ChalkActionButton testId="next-play-erase" variant="danger" label="Erase and Redraw" loading={cancelling} icon={<Zap size={16} aria-hidden="true" />} onClick={onErase} />
        )}
      </div>
    </ChalkCard>
  );
}

export { humanWhen };
